/* global trapFocus, removeTrapFocus */
/**
 * 集合页产品对比
 * - 产品卡片勾选、悬浮条、对比弹窗
 * - localStorage 持久化
 */
;(() => {
  const STORAGE_KEY = 'wd_clp_compare_v2'
  const MOBILE_MAX = 2
  const DESKTOP_MAX = 3
  const MSG_MAX = 'Die maximale Anzahl ausgewählter Produkte wurde erreicht.'
  const MSG_MIN = 'Bitte wählen Sie mindestens zwei Produkte zum Vergleich aus.'

  class CollectionCompare {
    constructor(root) {
      this.root = root
      this.configEl = document.getElementById('CollectionCompareConfig')
      this.config = this.parseConfig()
      this.productsMap = {}
      Object.entries(this.config.products || {}).forEach(([id, product]) => {
        const sanitized = this.sanitizeProduct({ ...(product || {}), id: product?.id || id })
        if (sanitized) this.productsMap[sanitized.id] = sanitized
      })
      this.selected = []
      this.collapsed = false
      this.diffOnly = false
      this.lastFocusedElement = null
      this.activeTabId = ''
      this.tabScrollLockUntil = 0

      this.bar = root.querySelector('[data-compare-bar]')
      this.slotsEl = root.querySelector('[data-compare-slots]')
      this.countEls = root.querySelectorAll('[data-compare-count]')
      this.maxEls = root.querySelectorAll('[data-compare-max]')
      this.modal = document.querySelector('[data-compare-modal]')
      this.modalDialog = this.modal?.querySelector('[role="dialog"]')
      this.modalProducts = this.modal?.querySelector('[data-compare-modal-products]')
      this.modalStickyHeader = this.modal?.querySelector('[data-compare-sticky-header]')
      this.modalStickyNames = this.modal?.querySelector('[data-compare-sticky-names]')
      this.modalTabs = this.modal?.querySelector('[data-compare-tabs]')
      this.modalGroups = this.modal?.querySelector('[data-compare-groups]')
      this.modalBody = this.modal?.querySelector('[data-compare-modal-body]')
      this.diffInput = this.modal?.querySelector('[data-compare-diff]')

      this.onResize = this.onResize.bind(this)
      this.onDocClick = this.onDocClick.bind(this)
      this.onGridUpdated = this.onGridUpdated.bind(this)
      this.onModalScroll = this.onModalScroll.bind(this)
      this.onModalKeydown = this.onModalKeydown.bind(this)

      this.init()
    }

    parseConfig() {
      const raw = (this.configEl?.textContent || '').trim()
      if (!raw) return { groups: [], products: {} }
      try {
        const data = JSON.parse(raw)
        if (!Array.isArray(data.groups)) data.groups = []
        if (!data.products || typeof data.products !== 'object') data.products = {}
        return data
      } catch (e) {
        console.error('CollectionCompareConfig JSON parse failed:', e, raw.slice(0, 500))
        return { groups: [], products: {} }
      }
    }

    ensureGroups() {
      const groups = Array.isArray(this.config.groups) ? this.config.groups : []
      const hasKeys = groups.some(g => Array.isArray(g.keys) && g.keys.length > 0)
      if (hasKeys) {
        this.config.groups = groups
        return
      }

      const keySet = new Map()
      Object.values(this.productsMap || {}).forEach(product => {
        Object.keys(product?.specs || {}).forEach(key => {
          if (!keySet.has(key)) {
            keySet.set(
              key,
              key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            )
          }
        })
      })
      this.selected.forEach(product => {
        Object.keys(product?.specs || {}).forEach(key => {
          if (!keySet.has(key)) {
            keySet.set(
              key,
              key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            )
          }
        })
      })

      this.config.groups = [
        {
          name: 'Allgemeine Spezifikationen',
          keys: Array.from(keySet.entries()).map(([key, label]) => ({ key, label }))
        }
      ]
    }

    syncSelectedFromMap() {
      this.selected = this.selected
        .map(item => {
          const latest = this.productsMap[String(item.id)]
          if (!latest) return this.sanitizeProduct(item)
          return this.sanitizeProduct(this.mergeProductData(item, latest))
        })
        .filter(Boolean)
    }

    get maxCount() {
      return window.matchMedia('(max-width: 767px)').matches ? MOBILE_MAX : DESKTOP_MAX
    }

    init() {
      this.loadState()
      this.bindEvents()
      this.mergeProductsFromCards()
      this.pruneInvalidSelected()
      this.syncCheckboxes()
      this.renderBar()
      this.updateMaxLabels()
      this.root.hidden = false
      this.root.setAttribute('aria-hidden', 'false')
    }

    loadState() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const data = JSON.parse(raw)
        if (!data || typeof data !== 'object') return

        const currentHandle = typeof this.config.collectionHandle === 'string' ? this.config.collectionHandle : ''
        const storedHandle = typeof data.collectionHandle === 'string' ? data.collectionHandle : ''

        // 切换集合页时不沿用上一集合的对比选择
        if (!currentHandle || storedHandle !== currentHandle) {
          this.selected = []
          this.collapsed = false
          this.saveState()
          return
        }

        this.collapsed = !!data.collapsed
        if (!Array.isArray(data.selected)) {
          this.selected = []
          return
        }

        this.selected = data.selected
          .map(item => this.sanitizeProduct(item))
          .filter(Boolean)
          .slice(0, DESKTOP_MAX)

        this.selected.forEach(product => {
          const existing = this.productsMap[product.id]
          // 页面/配置数据优先，localStorage 仅补缺，避免访客可控字段覆盖可信产品数据
          this.productsMap[product.id] = existing
            ? this.sanitizeProduct(this.mergeProductData(product, existing))
            : product
        })
      } catch {
        this.selected = []
      }
    }

    saveState() {
      try {
        const selected = this.selected.map(item => this.sanitizeProduct(item)).filter(Boolean)
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            selected,
            collapsed: this.collapsed,
            collectionHandle: typeof this.config.collectionHandle === 'string' ? this.config.collectionHandle : ''
          })
        )
      } catch {
        // localStorage 不可用时跳过持久化
      }
    }

    bindEvents() {
      document.addEventListener('click', this.onDocClick)
      window.addEventListener('resize', this.onResize)
      document.addEventListener('collection:grid:updated', this.onGridUpdated)
      document.addEventListener('load-more:complete', this.onGridUpdated)

      this.root.querySelector('[data-compare-clear]')?.addEventListener('click', () => this.clearAll())
      this.root.querySelector('[data-compare-collapse]')?.addEventListener('click', () => {
        this.collapsed = !this.collapsed
        this.saveState()
        this.renderBar()
      })
      this.root.querySelector('[data-compare-now]')?.addEventListener('click', () => this.openModal())
      this.slotsEl?.addEventListener('click', e => {
        const btn = e.target.closest('[data-compare-remove]')
        if (!btn || !this.slotsEl.contains(btn)) return
        e.stopPropagation()
        this.removeProduct(btn.getAttribute('data-compare-remove'))
      })
      this.modal?.querySelectorAll('[data-compare-modal-collapse]').forEach(el => {
        el.addEventListener('click', () => this.closeModal(false))
      })
      this.modal?.querySelector('[data-compare-modal-close]')?.addEventListener('click', () => this.closeModal(false))
      this.modalBody?.addEventListener('scroll', this.onModalScroll, { passive: true })

      this.diffInput?.addEventListener('change', () => {
        this.diffOnly = !!this.diffInput.checked
        this.renderTabs()
        this.renderGroups()
      })
    }

    onModalKeydown(event) {
      if (!this.modal?.classList.contains('is-open')) return
      if (event.key !== 'Escape' && event.code !== 'Escape') return
      event.preventDefault()
      this.closeModal(false)
    }

    onModalScroll() {
      if (!this.modalBody || !this.modalStickyNames || !this.modalProducts) return
      const threshold = this.modalProducts.offsetHeight - 24
      const show = this.modalBody.scrollTop > threshold
      this.modalStickyNames.hidden = !show
      this.modalStickyNames.classList.toggle('is-visible', show)
      this.modalStickyHeader?.classList.toggle('is-names-visible', show)

      // 点击 tab 触发的程序滚动期间，不覆盖手动选中的高亮
      if (Date.now() < this.tabScrollLockUntil) return

      const groups = Array.from(this.modalGroups?.querySelectorAll('[data-compare-group]') || [])
      if (!groups.length) return

      const stickyHeaderHeight = this.modalStickyHeader?.offsetHeight || 0
      const activeLine = this.modalBody.getBoundingClientRect().top + stickyHeaderHeight + 1
      let activeId = groups[0].getAttribute('data-compare-group')

      groups.forEach(group => {
        if (group.getBoundingClientRect().top <= activeLine) {
          activeId = group.getAttribute('data-compare-group')
        }
      })
      if (this.modalBody.scrollTop + this.modalBody.clientHeight >= this.modalBody.scrollHeight - 2) {
        activeId = groups[groups.length - 1].getAttribute('data-compare-group')
      }

      let activeTab = null
      this.modalTabs?.querySelectorAll('[data-compare-tab]').forEach(tab => {
        const active = tab.getAttribute('data-compare-tab') === activeId
        tab.classList.toggle('is-active', active)
        if (active) {
          tab.setAttribute('aria-current', 'true')
          activeTab = tab
        } else {
          tab.removeAttribute('aria-current')
        }
      })

      if (activeTab && activeId !== this.activeTabId) {
        this.activeTabId = activeId
        this.scrollTabIntoView(activeTab)
      }
    }

    scrollTabIntoView(tab) {
      if (!tab || !this.modalTabs) return
      const tabs = this.modalTabs
      const targetLeft = tab.offsetLeft - (tabs.clientWidth - tab.offsetWidth) / 2
      const maxLeft = Math.max(0, tabs.scrollWidth - tabs.clientWidth)
      tabs.scrollTo({
        left: Math.min(Math.max(0, targetLeft), maxLeft),
        behavior: 'smooth'
      })
    }

    onDocClick(event) {
      const compareCheck = event.target.closest('[data-compare-check]')
      if (compareCheck) {
        event.preventDefault()
        const checkbox = compareCheck.querySelector('[data-compare-checkbox]')
        const productId = String(checkbox?.getAttribute('data-product-id') || '')
        if (!productId) return

        if (compareCheck.classList.contains('is-disabled')) {
          this.toast(MSG_MAX)
          return
        }

        if (!this.isSafeId(productId)) return

        const raw = compareCheck.getAttribute('data-compare-product-json')
        if (raw) {
          try {
            const data = this.sanitizeProduct(JSON.parse(raw))
            if (data?.id) {
              const existing = this.productsMap[data.id] || {}
              this.productsMap[data.id] = this.mergeProductData(existing, data)
            }
          } catch {
            // 忽略无效的产品对比 JSON
          }
        }

        const isChecked = this.selected.some(p => String(p.id) === productId)
        if (!isChecked && this.selected.length >= this.maxCount) {
          this.toast(MSG_MAX)
          return
        }

        if (isChecked) {
          this.removeProduct(productId)
        } else {
          this.addProduct(productId)
        }
        return
      }

      const buyNow = event.target.closest('[data-compare-buy-now]')
      if (buyNow) {
        const variantId = buyNow.getAttribute('data-variant-id')
        this.track('wd_buy_now', {
          event_source: this.eventSource(),
          event_category: 'collection-compare',
          event_action: buyNow.getAttribute('data-sku') || ''
        })
        if (variantId) {
          window.location.href = `/cart/${variantId}:1`
        }
        return
      }

      const pdpLink = event.target.closest(
        '.wd-collection-compare__product-image, .wd-collection-compare__product-title, .wd-collection-compare__sticky-name'
      )
      if (pdpLink) {
        this.track('wd_enter_pdp_click', { event_source: this.eventSource() })
        return
      }

      const groupToggle = event.target.closest('[data-compare-group-toggle]')
      if (groupToggle) {
        const group = groupToggle.closest('[data-compare-group]')
        group?.classList.toggle('is-collapsed')
        return
      }

      const tab = event.target.closest('[data-compare-tab]')
      if (tab) {
        const id = tab.getAttribute('data-compare-tab')
        const target = this.modalGroups?.querySelector(`[data-compare-group="${id}"]`)
        if (!target || !this.modalBody) return

        // 点击分类导航时，若该分组为收起状态则展开
        target.classList.remove('is-collapsed')

        this.modalTabs?.querySelectorAll('[data-compare-tab]').forEach(item => {
          const active = item === tab
          item.classList.toggle('is-active', active)
          if (active) item.setAttribute('aria-current', 'true')
          else item.removeAttribute('aria-current')
        })
        this.activeTabId = id
        this.tabScrollLockUntil = Date.now() + 600
        this.scrollTabIntoView(tab)

        const stickyHeaderHeight = this.modalStickyHeader?.offsetHeight || 0
        const targetTop =
          this.modalBody.scrollTop +
          target.getBoundingClientRect().top -
          this.modalBody.getBoundingClientRect().top -
          stickyHeaderHeight

        this.modalBody.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth'
        })
      }
    }

    onResize() {
      this.updateMaxLabels()
      while (this.selected.length > this.maxCount) {
        this.selected.pop()
      }
      this.saveState()
      this.syncCheckboxes()
      this.renderBar()
      if (this.modal?.classList.contains('is-open')) {
        this.renderModal()
      }
    }

    onGridUpdated() {
      this.mergeConfigFromDom()
      this.mergeProductsFromCards()
      this.syncSelectedFromMap()
      this.syncCheckboxes()
      this.renderBar()
    }

    mergeConfigFromDom() {
      const el = document.getElementById('CollectionCompareConfig')
      if (!el) return
      try {
        const next = JSON.parse(el.textContent || '{}')
        if (!next || typeof next !== 'object') return
        this.config = {
          ...this.config,
          ...next,
          groups: Array.isArray(next.groups) ? next.groups : this.config.groups || [],
          products: next.products && typeof next.products === 'object' ? next.products : {}
        }
        Object.entries(this.config.products || {}).forEach(([id, product]) => {
          const sanitized = this.sanitizeProduct({ ...(product || {}), id: product?.id || id })
          if (!sanitized) return
          const existing = this.productsMap[sanitized.id] || {}
          this.productsMap[sanitized.id] = this.mergeProductData(existing, sanitized)
        })
      } catch {
        // 保留当前配置，忽略无效的更新数据
      }
    }

    mergeProductsFromCards() {
      document.querySelectorAll('[data-compare-product-json]').forEach(el => {
        try {
          const data = this.sanitizeProduct(JSON.parse(el.getAttribute('data-compare-product-json') || '{}'))
          if (!data?.id) return
          const existing = this.productsMap[data.id] || {}
          this.productsMap[data.id] = this.mergeProductData(existing, data)
        } catch {
          // 忽略无效的产品对比 JSON
        }
      })
    }

    pruneInvalidSelected() {
      this.selected = this.selected.map(item => this.sanitizeProduct(item)).filter(Boolean)
      if (this.selected.length > this.maxCount) {
        this.selected = this.selected.slice(0, this.maxCount)
      }
      this.saveState()
    }

    mergeProductData(existing = {}, next = {}) {
      return {
        ...existing,
        ...next,
        id: next.id || existing.id,
        specs: { ...(existing.specs || {}), ...(next.specs || {}) }
      }
    }

    isSafeId(value) {
      if (typeof value !== 'string' && typeof value !== 'number') return false
      return /^\d{1,20}$/.test(String(value))
    }

    isSafeHref(url) {
      if (typeof url !== 'string' || !url.trim()) return false
      const value = url.trim()
      if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true
      try {
        const parsed = new URL(value, window.location.origin)
        return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.origin === window.location.origin
      } catch {
        return false
      }
    }

    // 富文本内链：仅站内链接 + https://cdn.shopify.com/
    isSafeRichTextHref(url) {
      if (typeof url !== 'string' || !url.trim()) return false
      const value = url.trim()
      if (value.startsWith('#') && !value.includes(':')) return true
      if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true
      try {
        const parsed = new URL(value, window.location.origin)
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false
        if (parsed.origin === window.location.origin) return true
        return parsed.protocol === 'https:' && parsed.hostname === 'cdn.shopify.com'
      } catch {
        return false
      }
    }

    isSafeImageUrl(url) {
      if (typeof url !== 'string' || !url.trim()) return false
      const value = url.trim()
      if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true
      try {
        const parsed = new URL(value, window.location.origin)
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false
        if (parsed.origin === window.location.origin) return true
        return /(^|\.)(?:shopify\.com|shopifycdn\.com)$/i.test(parsed.hostname)
      } catch {
        return false
      }
    }

    sanitizeSpecs(specs) {
      if (!specs || typeof specs !== 'object' || Array.isArray(specs)) return {}
      const result = {}
      Object.keys(specs).forEach(key => {
        if (!/^[a-z0-9_]{1,64}$/i.test(key)) return
        const value = specs[key]
        if (value == null) return
        if (typeof value === 'string') {
          result[key] = value.slice(0, 5000)
          return
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
          result[key] = String(value)
        }
      })
      return result
    }

    sanitizeProduct(raw) {
      if (!raw || typeof raw !== 'object') return null
      const id = this.isSafeId(raw.id) ? String(raw.id) : ''
      if (!id) return null

      const variantId = this.isSafeId(raw.variantId) ? String(raw.variantId) : ''
      const url = this.isSafeHref(raw.url) ? raw.url.trim() : '#'
      const image = this.isSafeImageUrl(raw.image) ? raw.image.trim() : ''

      return {
        id,
        handle: typeof raw.handle === 'string' ? raw.handle.slice(0, 200) : '',
        title: typeof raw.title === 'string' ? raw.title.slice(0, 300) : '',
        url,
        image,
        price: typeof raw.price === 'string' ? raw.price.slice(0, 40) : '',
        compareAtPrice: typeof raw.compareAtPrice === 'string' ? raw.compareAtPrice.slice(0, 40) : '',
        discountBadge: typeof raw.discountBadge === 'string' ? raw.discountBadge.slice(0, 40) : '',
        discountCode: typeof raw.discountCode === 'string' ? raw.discountCode.slice(0, 80) : '',
        variantId,
        sku: typeof raw.sku === 'string' ? raw.sku.slice(0, 80) : '',
        available: raw.available !== false,
        specs: this.sanitizeSpecs(raw.specs)
      }
    }

    sanitizeRichText(html) {
      if (typeof html !== 'string' || !html.trim()) return ''
      let source = html
      if (typeof window.DOMPurify?.sanitize === 'function') {
        source = window.DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li', 'a', 'span', 'div'],
          ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
        })
      }

      const doc = new DOMParser().parseFromString(source, 'text/html')
      const allowedTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'A', 'SPAN', 'DIV'])

      const clean = node => {
        ;[...node.childNodes].forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) return
          if (child.nodeType !== Node.ELEMENT_NODE) {
            child.remove()
            return
          }
          if (!allowedTags.has(child.tagName)) {
            const text = doc.createTextNode(child.textContent || '')
            child.replaceWith(text)
            return
          }
          ;[...child.attributes].forEach(attr => {
            const name = attr.name.toLowerCase()
            if (child.tagName === 'A' && name === 'href' && this.isSafeRichTextHref(attr.value)) {
              child.setAttribute('href', attr.value.trim())
              child.setAttribute('rel', 'noopener noreferrer')
              return
            }
            if (child.tagName === 'A' && name === 'target' && attr.value === '_blank') return
            if (name === 'class' && /^[a-z0-9\s_-]{1,80}$/i.test(attr.value)) return
            child.removeAttribute(attr.name)
          })
          if (child.tagName === 'A' && !child.getAttribute('href')) {
            const text = doc.createTextNode(child.textContent || '')
            child.replaceWith(text)
            return
          }
          clean(child)
        })
      }

      clean(doc.body)
      return doc.body.innerHTML
    }

    formatSpecValue(value) {
      if (value == null || value === '') return '-'
      const text = String(value).trim()
      if (!text) return '-'
      if (!text.includes('<')) return this.escapeHtml(text)
      const sanitized = this.sanitizeRichText(text)
      return sanitized || this.escapeHtml(text.replace(/<[^>]*>/g, ''))
    }

    getProduct(id) {
      return this.productsMap[String(id)] || this.selected.find(p => String(p.id) === String(id))
    }

    addProduct(id) {
      if (!this.isSafeId(id)) return
      const product = this.sanitizeProduct(this.getProduct(id))
      if (!product) return
      if (this.selected.some(p => String(p.id) === String(id))) return
      if (this.selected.length >= this.maxCount) {
        this.toast(MSG_MAX)
        return
      }

      this.selected.push(product)
      this.collapsed = false
      this.saveState()
      this.syncCheckboxes()
      this.renderBar()
      this.track('wd_collection_compare_select', {
        event_source: this.eventSource(),
        event_action: product.sku || String(id)
      })
    }

    removeProduct(id) {
      const removed = this.selected.find(p => String(p.id) === String(id))
      this.selected = this.selected.filter(p => String(p.id) !== String(id))
      this.saveState()
      this.syncCheckboxes()
      this.renderBar()
      if (removed) {
        this.track('wd_collection_compare_unselect', {
          event_source: this.eventSource(),
          event_action: removed.sku || String(id)
        })
      }
      if (this.modal?.classList.contains('is-open')) {
        if (this.selected.length < 2) this.closeModal(false)
        else this.renderModal()
      }
    }

    clearAll() {
      this.selected.forEach(product => {
        this.track('wd_collection_compare_unselect', {
          event_source: this.eventSource(),
          event_action: product.sku || String(product.id)
        })
      })
      this.selected = []
      this.collapsed = false
      this.saveState()
      this.syncCheckboxes()
      this.renderBar()
      this.closeModal(false)
    }

    syncCheckboxes() {
      const maxed = this.selected.length >= this.maxCount
      document.querySelectorAll('[data-compare-checkbox]').forEach(input => {
        const id = String(input.getAttribute('data-product-id') || '')
        const checked = this.selected.some(p => String(p.id) === id)
        input.checked = checked
        const label = input.closest('[data-compare-check]')
        label?.classList.toggle('is-checked', checked)
        label?.classList.toggle('is-disabled', !checked && maxed)
        input.disabled = !checked && maxed
      })
    }

    updateMaxLabels() {
      this.maxEls.forEach(el => {
        el.textContent = String(this.maxCount)
      })
    }

    renderBar() {
      const show = this.selected.length > 0
      this.root.classList.toggle('is-visible', show)
      this.root.classList.toggle('is-collapsed', this.collapsed)
      this.root.setAttribute('aria-hidden', show ? 'false' : 'true')

      this.countEls.forEach(el => {
        el.textContent = String(this.selected.length)
      })
      this.updateMaxLabels()

      if (!this.slotsEl) return
      if (this.collapsed) {
        this.slotsEl.innerHTML = ''
        return
      }

      for (let i = 0; i < this.maxCount; i++) {
        const product = this.selected[i]
        const nextId = product ? String(product.id) : ''
        const existing = this.slotsEl.children[i]
        const existingId = existing?.getAttribute('data-product-id') || ''

        if (existing && existingId === nextId) continue

        const node = product ? this.createFilledSlot(product) : this.createEmptySlot()
        if (existing) existing.replaceWith(node)
        else this.slotsEl.appendChild(node)
      }

      while (this.slotsEl.children.length > this.maxCount) {
        this.slotsEl.lastElementChild.remove()
      }
    }

    createEmptySlot() {
      const el = document.createElement('div')
      el.className = 'wd-collection-compare__slot is-empty'
      el.setAttribute('data-compare-plus', '')
      el.setAttribute('aria-hidden', 'true')
      return el
    }

    createFilledSlot(product) {
      const safeId = this.escapeAttr(String(product.id))
      const safeImage = this.isSafeImageUrl(product.image) ? this.escapeAttr(product.image) : ''
      const wrap = document.createElement('div')
      wrap.innerHTML = `
        <div class="wd-collection-compare__slot is-filled" data-product-id="${safeId}">
          <button type="button" class="wd-collection-compare__slot-remove" data-compare-remove="${safeId}" aria-label="Entfernen">
            ${this.closeIcon()}
          </button>
          <div class="wd-collection-compare__slot-card">
            ${safeImage ? `<img src="${safeImage}" alt="${this.escapeAttr(product.title)}" width="104" height="104" loading="lazy" decoding="async">` : ''}
            <p class="wd-collection-compare__slot-title">${this.escapeHtml(product.title || '')}</p>
          </div>
        </div>
      `.trim()
      return wrap.firstElementChild
    }

    openModal() {
      if (this.selected.length < 2) {
        this.toast(MSG_MIN)
        return
      }

      if (!this.modal) {
        this.modal = document.querySelector('[data-compare-modal]')
        if (!this.modal) return
        this.modalDialog = this.modal.querySelector('[role="dialog"]')
        this.modalProducts = this.modal.querySelector('[data-compare-modal-products]')
        this.modalStickyHeader = this.modal.querySelector('[data-compare-sticky-header]')
        this.modalStickyNames = this.modal.querySelector('[data-compare-sticky-names]')
        this.modalTabs = this.modal.querySelector('[data-compare-tabs]')
        this.modalGroups = this.modal.querySelector('[data-compare-groups]')
        this.modalBody = this.modal.querySelector('[data-compare-modal-body]')
        this.diffInput = this.modal.querySelector('[data-compare-diff]')
      }

      this.lastFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : this.root.querySelector('[data-compare-now]')

      this.track('wd_collection_compare_click', {
        event_source: this.eventSource(),
        event_action: this.selectedSkus()
      })

      // 先藏浮窗并打开弹窗壳，避免 renderModal 耗时导致浮窗残留
      this.root.classList.add('is-modal-open')
      this.modal.hidden = false
      this.modal.removeAttribute('hidden')
      this.modal.classList.add('is-open')
      this.modal.setAttribute('aria-hidden', 'false')
      document.documentElement.classList.add('wd-compare-modal-open')

      try {
        this.mergeProductsFromCards()
        this.syncSelectedFromMap()
        this.ensureGroups()
        this.renderModal()
      } catch (e) {
        console.error('Collection compare modal render failed:', e)
      }

      document.addEventListener('keydown', this.onModalKeydown)
      if (this.modalBody) this.modalBody.scrollTop = 0
      requestAnimationFrame(() => {
        this.onModalScroll()
        this.trapModalFocus()
      })
    }

    trapModalFocus() {
      if (!this.modalDialog) {
        this.modalDialog = this.modal?.querySelector('[role="dialog"]')
      }
      const focusTarget = this.modal?.querySelector('[data-compare-modal-close]') || this.modalDialog
      if (!this.modalDialog || !focusTarget) return

      if (typeof trapFocus === 'function') {
        trapFocus(this.modalDialog, focusTarget)
      } else {
        focusTarget.focus()
      }
    }

    closeModal(clear) {
      if (!this.modal) return
      this.modal.classList.remove('is-open')
      this.modal.hidden = true
      this.modal.setAttribute('hidden', '')
      this.modal.setAttribute('aria-hidden', 'true')
      document.documentElement.classList.remove('wd-compare-modal-open')
      this.root.classList.remove('is-modal-open')
      document.removeEventListener('keydown', this.onModalKeydown)
      if (this.modalStickyNames) {
        this.modalStickyNames.hidden = true
        this.modalStickyNames.classList.remove('is-visible')
      }
      this.modalStickyHeader?.classList.remove('is-names-visible')

      const restoreTarget = this.lastFocusedElement
      this.lastFocusedElement = null
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(restoreTarget)
      } else if (restoreTarget && typeof restoreTarget.focus === 'function') {
        restoreTarget.focus()
      }

      if (clear) this.clearAll()
    }

    renderModal() {
      const cols = String(Math.max(this.selected.length, 1))
      this.modal?.style.setProperty('--compare-cols', cols)
      this.renderModalProducts()
      this.renderStickyNames()
      this.renderTabs()
      this.renderGroups()
    }

    renderStickyNames() {
      if (!this.modalStickyNames) return
      this.modalStickyNames.innerHTML = this.selected
        .map(product => {
          const href = this.isSafeHref(product.url) ? this.escapeAttr(product.url) : '#'
          return `<a class="wd-collection-compare__sticky-name" href="${href}"><span class="wd-collection-compare__sticky-name-text">${this.escapeHtml(product.title || '')}</span></a>`
        })
        .join('')
    }

    renderModalProducts() {
      if (!this.modalProducts) return
      this.modalProducts.innerHTML = this.selected
        .map(product => {
          const soldOut = product.available === false
          const safeId = this.escapeAttr(String(product.id))
          const safeVariantId = this.escapeAttr(String(product.variantId || ''))
          const href = this.isSafeHref(product.url) ? this.escapeAttr(product.url) : '#'
          const safeImage = this.isSafeImageUrl(product.image) ? this.escapeAttr(product.image) : ''
          return `
            <div class="wd-collection-compare__product" data-product-id="${safeId}">
              <a class="wd-collection-compare__product-image" href="${href}" aria-label="${this.escapeAttr(product.title || 'Produkt ansehen')}">
                ${safeImage ? `<img src="${safeImage}" alt="${this.escapeAttr(product.title)}" width="120" height="120" loading="lazy">` : ''}
              </a>
              <a class="wd-collection-compare__product-title" href="${href}">${this.escapeHtml(product.title || '')}</a>
              <div class="wd-collection-compare__product-price">
                <span class="wd-collection-compare__price">${this.escapeHtml(product.price || '')}</span>
                ${product.compareAtPrice ? `<span class="wd-collection-compare__compare-at del-container">${this.escapeHtml(product.compareAtPrice)}</span>` : ''}
                ${product.discountBadge ? `<span class="wd-collection-compare__badge">${this.escapeHtml(product.discountBadge)}</span>` : ''}
              </div>
              <div class="wd-collection-compare__code-slot${product.discountCode ? '' : ' is-empty'}">
                ${
                  product.discountCode
                    ? `<button
                        type="button"
                        class="component-code-button wd-collection-compare__code"
                        data-value="${this.escapeAttr(product.discountCode)}"
                        aria-label="Rabattcode kopieren"
                      >
                        <span>Code:</span>
                        <p translate="no">${this.escapeHtml(product.discountCode)}</p>
                        ${this.copyIcon()}
                        <div class="component-copied-popup" role="status" aria-live="polite">
                          <svg class="component-copied-popup-svg" xmlns="http://www.w3.org/2000/svg" width="12" height="10" fill="none" viewBox="0 0 12 10" aria-hidden="true">
                            <path fill="#999" d="m6 0 6 10H0z"/>
                          </svg>
                          <div>Kopiert</div>
                        </div>
                      </button>`
                    : '<span aria-hidden="true">&nbsp;</span>'
                }
              </div>
              ${
                soldOut
                  ? `<button type="button" class="primary-button-filled wd-collection-compare__atc is-soldout" disabled>Ausverkauft</button>
                    <span class="wd-collection-compare__buy-now is-empty" aria-hidden="true">&nbsp;</span>`
                  : `<button
                      type="button"
                      class="primary-button-filled component-atc-button wd-collection-compare__atc"
                      data-atc-button="true"
                      data-compare-atc
                      data-atc-id="${safeVariantId}"
                      data-atc-qty="1"
                      data-atc-text="In den Warenkorb"
                      data-atc-available="true"
                      aria-disabled="false"
                      data-event-category="collection-compare"
                      data-event-sku="${this.escapeAttr(product.sku || '')}"
                      data-product-id="${safeId}"
                      data-sku="${this.escapeAttr(product.sku || '')}"
                    >
                      <span class="component-atc-button__text">In den Warenkorb</span>
                      <span class="loading__spinner hidden" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20">
                          <path fill="currentColor" d="M10 0a1.114 1.114 0 0 1 .786 1.897 1.1 1.1 0 0 1-.787.325 7.8 7.8 0 0 0-2.977.592 7.78 7.78 0 1 0 10.756 7.187A1.11 1.11 0 0 1 20 10a10.04 10.04 0 0 1-1.685 5.555 10 10 0 0 1-1.97 2.174 10 10 0 0 1-1.631 1.091 10.1 10.1 0 0 1-3.735 1.133 10 10 0 0 1-1.96 0 10 10 0 0 1-1.923-.384 10 10 0 0 1-2.238-.992 10.1 10.1 0 0 1-2.268-1.862 10 10 0 0 1-2.16-3.812A10.04 10.04 0 0 1 .19 8.05a10 10 0 0 1 .988-2.764 10.1 10.1 0 0 1 2.477-3.015A9.96 9.96 0 0 1 9.999 0"/>
                        </svg>
                      </span>
                    </button>
                    <button
                      type="button"
                      class="wd-collection-compare__buy-now text-button"
                      data-compare-buy-now
                      data-variant-id="${safeVariantId}"
                      data-product-id="${safeId}"
                      data-sku="${this.escapeAttr(product.sku || '')}"
                    >Jetzt kaufen</button>`
              }
            </div>
          `
        })
        .join('')
    }

    renderTabs() {
      if (!this.modalTabs) return
      const groups = this.config.groups || []
      const visibleGroups = groups
        .map((group, index) => ({ group, index }))
        .filter(({ group }) =>
          (group.keys || []).some(item => {
            const values = this.selected.map(product => this.normalizeSpec(product?.specs?.[item.key]))
            if (values.every(value => value === '-')) return false
            if (!this.diffOnly) return true
            return values.some(value => value !== values[0])
          })
        )

      this.modalTabs.innerHTML = visibleGroups
        .map(
          ({ group, index }, visibleIndex) =>
            `<button type="button" class="wd-collection-compare__tab${visibleIndex === 0 ? ' is-active' : ''}" data-compare-tab="group-${index}"${visibleIndex === 0 ? ' aria-current="true"' : ''}>${this.escapeHtml(group.name || '')}</button>`
        )
        .join('')
      this.activeTabId = visibleGroups.length ? `group-${visibleGroups[0].index}` : ''
    }

    renderGroups() {
      if (!this.modalGroups) return
      const groups = this.config.groups || []
      const products = this.selected

      this.modalGroups.innerHTML = groups
        .map((group, groupIndex) => {
          const keys = (group.keys || []).filter(item => {
            const values = products.map(p => this.normalizeSpec(p?.specs?.[item.key]))
            if (values.every(v => v === '-')) return false
            if (!this.diffOnly) return true
            return values.some(v => v !== values[0])
          })

          if (!keys.length) return ''

          const rows = keys
            .map(item => {
              const cells = products
                .map((p, idx) => {
                  const labelHtml =
                    idx === 0
                      ? `<p class="wd-collection-compare__spec-label">${this.escapeHtml(item.label || item.key)}</p>`
                      : `<p class="wd-collection-compare__spec-label is-hidden">${this.escapeHtml(item.label || item.key)}</p>`
                  return `
                    <div class="wd-collection-compare__spec-cell">
                      ${labelHtml}
                      <div class="wd-collection-compare__spec-value">${this.formatSpecValue(p?.specs?.[item.key])}</div>
                    </div>
                  `
                })
                .join('')
              return `<div class="wd-collection-compare__spec-row">${cells}</div>`
            })
            .join('')

          return `
            <section class="wd-collection-compare__group" data-compare-group="group-${groupIndex}">
              <button type="button" class="wd-collection-compare__group-head" data-compare-group-toggle>
                <span>${this.escapeHtml(group.name || '')}</span>
                <span class="wd-collection-compare__group-chevron">${this.chevronIcon()}</span>
              </button>
              <div class="wd-collection-compare__group-body">
                ${rows}
              </div>
            </section>
          `
        })
        .join('')
    }

    normalizeSpec(value) {
      if (value == null || value === '') return '-'
      const text = String(value)
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim()
      return text || '-'
    }

    toast(message) {
      if (typeof window.showMessage === 'function') {
        window.showMessage(message, 'warning', 3000)
      } else if (typeof window.showToast === 'function') {
        window.showToast({ message, type: 'warning' })
      } else {
        console.warn(message)
      }
    }

    track(eventName, payload = {}) {
      try {
        if (typeof window.WDSendEvent === 'function') {
          window.WDSendEvent(eventName, payload)
          return
        }
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: eventName,
          ...payload
        })
      } catch {
        // 数据追踪失败不影响页面交互
      }
    }

    eventSource() {
      return window.location.pathname
    }

    selectedSkus() {
      return this.selected
        .map(p => p.sku || '')
        .filter(Boolean)
        .join(',')
    }

    async copyText(text) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
          return
        }
      } catch {
        // Clipboard API 失败时使用下方兼容方案
      }
      const input = document.createElement('textarea')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      input.remove()
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }

    escapeAttr(str) {
      return this.escapeHtml(str).replace(/`/g, '&#96;')
    }

    closeIcon() {
      return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" stroke-width="1.2"/></svg>'
    }

    chevronIcon() {
      return '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7" fill="none"><path d="M0.441895 5.625L5.44189 0.625L10.4419 5.625" stroke="currentColor" stroke-width="1.25" stroke-linejoin="round"/></svg>'
    }

    copyIcon() {
      return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M15.6218 9.64575C15.75 9.336 15.75 8.94225 15.75 8.15625C15.75 7.37025 15.75 6.9765 15.6218 6.66675C15.5369 6.46195 15.4126 6.27586 15.2559 6.11912C15.0991 5.96238 14.9131 5.83806 14.7083 5.75325C14.3985 5.625 14.0047 5.625 13.2188 5.625H8.325C7.38 5.625 6.9075 5.625 6.54675 5.80875C6.22895 5.97058 5.97058 6.22895 5.80875 6.54675C5.625 6.90675 5.625 7.37925 5.625 8.325V13.2188C5.625 14.0047 5.625 14.3985 5.75325 14.7083C5.925 15.1215 6.2535 15.4508 6.66675 15.6218C6.9765 15.75 7.37025 15.75 8.15625 15.75C8.94225 15.75 9.336 15.75 9.64575 15.6218M15.6218 9.64575C15.5369 9.85055 15.4126 10.0366 15.2559 10.1934C15.0991 10.3501 14.9131 10.4744 14.7083 10.5593C14.3985 10.6875 14.0047 10.6875 13.2188 10.6875C12.4328 10.6875 12.039 10.6875 11.7292 10.8157C11.5244 10.9006 11.3384 11.0249 11.1816 11.1816C11.0249 11.3384 10.9006 11.5244 10.8157 11.7292C10.6875 12.039 10.6875 12.4328 10.6875 13.2188C10.6875 14.0047 10.6875 14.3985 10.5593 14.7083C10.4744 14.9131 10.3501 15.0991 10.1934 15.2559C10.0366 15.4126 9.85055 15.5369 9.64575 15.6218M15.6218 9.64575C15.2184 11.037 14.4763 12.3065 13.4619 13.3405C12.4475 14.3745 11.1925 15.1408 9.80925 15.5707L9.64575 15.6218M12.375 5.625V4.95C12.375 4.005 12.375 3.5325 12.1912 3.17175C12.0296 2.85406 11.7715 2.59571 11.454 2.43375C11.0925 2.25 10.62 2.25 9.675 2.25H4.95C4.005 2.25 3.5325 2.25 3.17175 2.43375C2.85395 2.59558 2.59558 2.85395 2.43375 3.17175C2.25 3.53175 2.25 4.00425 2.25 4.95V9.675C2.25 10.62 2.25 11.0925 2.43375 11.4532C2.59575 11.7712 2.85375 12.0292 3.17175 12.1912C3.53175 12.375 4.005 12.375 4.95075 12.375H5.625" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    }
  }

  function initCollectionCompare() {
    const root = document.querySelector('[data-collection-compare]')
    if (!root || root.dataset.compareInited === 'true') return
    root.dataset.compareInited = 'true'
    window.__wdCollectionCompare = new CollectionCompare(root)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionCompare)
  } else {
    initCollectionCompare()
  }

  document.addEventListener('collection:grid:updated', () => {
    if (window.__wdCollectionCompare) {
      window.__wdCollectionCompare.onGridUpdated()
    } else {
      initCollectionCompare()
    }
  })
})()
